import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Image from "components/base/Image";
import Page from "components/base/Page";
import Spinner, { InlineSpinner } from "components/base/Spinner";
import CreateVideoForm from "./CreateVideoForm";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import VideoForm from "./VideoForm";
import { FETCH_ALL_VIDEOS, REORDER_VIDEOS } from "./graphql";

function VideoList() {
  const { loading, error, data } = useQuery(FETCH_ALL_VIDEOS);
  const videoFormModal = useModals();
  const createVideoModal = useModals();
  const [reorderVideos, reorderVideosRes] = useMutation(REORDER_VIDEOS, {
    onCompleted() {
      Alert("success", "Ordering updated");
    },
    onError(error) {
      Alert("error", error.message);
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const sortedVideos = [...data.allVideos].sort(
    (a, b) => a.ordering - b.ordering,
  );
  const videos = [
    ...sortedVideos.filter((i) => i.active),
    ...sortedVideos.filter((i) => !i.active),
  ];

  function onDragEnd(result) {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const ordering = reorder(
      videos,
      result.source.index,
      result.destination.index,
    ).map((i, index) => ({ id: i.id, ordering: index + 1 }));
    reorderVideos({ variables: { ordering } });
  }

  return (
    <Page
      title="Videos"
      rightButtons={
        <Button
          onClick={() => {
            createVideoModal.present({
              title: "Create New Video",
              maxWidth: "max-w-2xl",
              center: true,
              children: <CreateVideoForm onClose={createVideoModal.hide} />,
            });
          }}
        >
          + Create Video
        </Button>
      }
    >
      <div className="m-6">
        <div className="opacity-70 space-y-2">
          <p>
            Please directly click the button on the top right to upload videos.
          </p>
        </div>
      </div>

      <div className="p-6 relative">
        {reorderVideosRes.loading ? (
          <div className="fixed inset-0 z-10 flex justify-center items-center md:left-56 bg-gray-50 bg-opacity-50 dark:bg-gray-800 dark:bg-opacity-80">
            <InlineSpinner text="Saving..." />
          </div>
        ) : null}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                ref={provided.innerRef}
              >
                {videos.map((video, index) => (
                  <Draggable
                    key={video.id}
                    draggableId={video.id}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={video.active ? "" : " opacity-40"}
                      >
                        <VideoCard
                          video={video}
                          onClick={() => {
                            videoFormModal.present({
                              title: "Video",
                              maxWidth: "max-w-6xl",
                              center: true,
                              children: (
                                <VideoForm
                                  video={video}
                                  hide={videoFormModal.hide}
                                />
                              ),
                            });
                          }}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </Page>
  );
}

const VideoCard = ({ video, onClick }) => {
  const thumb =
    video.sources.length > 0 ? video.sources.map((i) => i.thumbSrc)[0] : null;
  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden hover:shadow-lg hover:scale-105 transition-all duration-200 cursor-pointer"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="relative w-full aspect-video bg-gray-100 dark:bg-gray-700">
        {thumb ? (
          <Image
            className="w-full h-full"
            size="w-full h-full"
            bgStyle="bg-center bg-cover bg-no-repeat"
            src={thumb}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
            <svg
              className="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <h4
          className="font-medium text-gray-900 dark:text-white truncate"
          title={video.name}
        >
          {video.name}
        </h4>
        {video.description && (
          <p
            className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2"
            title={video.description}
          >
            {video.description}
          </p>
        )}
      </div>
    </div>
  );
};

export default VideoList;
